import React, { useEffect, useRef, useState } from 'react';
import { AdSlot, loadAdsConfig, isActiveStoreFreePlan } from '../lib/adsConfig';

interface Props {
  slot: AdSlot;
  // Landing/platform surfaces always show configured zones; in-app zones
  // only render for stores on the free plan.
  requireFreePlan?: boolean;
  className?: string;
}

export function AdZone({ slot, requireFreePlan = true, className = '' }: Props) {
  const [html, setHtml] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const cfg = await loadAdsConfig();
      if (!alive || !cfg?.enabled || !cfg.zones[slot]) return;
      if (requireFreePlan && !(await isActiveStoreFreePlan())) return;
      if (alive) setHtml(cfg.zones[slot]!);
    })();
    return () => { alive = false; };
  }, [slot, requireFreePlan]);

  // innerHTML never executes <script> — re-create script nodes so real ad
  // network tags actually run (config is master-admin controlled only).
  useEffect(() => {
    if (!html || !ref.current) return;
    ref.current.innerHTML = html;
    ref.current.querySelectorAll('script').forEach((old) => {
      const s = document.createElement('script');
      [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
      s.textContent = old.textContent;
      old.replaceWith(s);
    });
  }, [html]);

  // SDK 'custom_code' creatives render inside a sandboxed iframe sized by the
  // creative's declared height — a wrong height leaves dead space inside the
  // frame. Shrink-wrap: replay the same srcdoc in a hidden same-origin probe
  // (scripts blocked) and clamp the real iframe to the actual content height.
  // Never grows past the declared height; re-measures on width changes.
  useEffect(() => {
    if (!html || !ref.current) return;
    const root = ref.current;
    const probes = new Set<HTMLIFrameElement>();

    const measure = (f: HTMLIFrameElement) => {
      const srcdoc = f.srcdoc || f.getAttribute('srcdoc');
      const declared = parseFloat(f.style.height) || 0;
      const width = f.getBoundingClientRect().width || root.getBoundingClientRect().width;
      if (!srcdoc || declared <= 0 || width <= 0) return;
      const probe = document.createElement('iframe');
      probe.setAttribute('sandbox', 'allow-same-origin');
      probe.style.cssText = `position:absolute;left:-9999px;top:0;visibility:hidden;pointer-events:none;border:0;width:${width}px;height:20px;`;
      probe.srcdoc = srcdoc;
      probes.add(probe);
      document.body.appendChild(probe);
      const apply = () => {
        const h = probe.contentDocument?.documentElement?.scrollHeight ?? 0;
        if (h >= 60) f.style.height = `${Math.min(declared, h)}px`;
      };
      probe.addEventListener('load', () => {
        apply();
        setTimeout(() => { apply(); probe.remove(); probes.delete(probe); }, 400);
      });
      setTimeout(() => { probe.remove(); probes.delete(probe); }, 6000);
    };

    const seen = new Map<HTMLIFrameElement, number>();
    const scan = (n: ParentNode) => n.querySelectorAll<HTMLIFrameElement>('iframe[srcdoc]').forEach((f) => {
      if (!seen.has(f)) { seen.set(f, f.clientWidth); measure(f); }
    });
    const mo = new MutationObserver((muts) => muts.forEach((m) =>
      m.addedNodes.forEach((n) => {
        if (n instanceof HTMLIFrameElement) scan(root);
        else if (n instanceof HTMLElement) scan(n);
      })));
    mo.observe(root, { childList: true, subtree: true });
    scan(root);

    // Width changes (rotation, breakpoints) alter the creative's height —
    // re-measure when a frame we already sized gets resized.
    const ro = new ResizeObserver((entries) => entries.forEach((e) => {
      const f = e.target as HTMLIFrameElement;
      const w = f.clientWidth;
      if (seen.has(f) && Math.abs(w - (seen.get(f) ?? 0)) > 8) { seen.set(f, w); measure(f); }
    }));

    const watch = () => root.querySelectorAll<HTMLIFrameElement>('iframe[srcdoc]').forEach((f) => ro.observe(f));
    watch();
    const mo2 = new MutationObserver(watch);
    mo2.observe(root, { childList: true, subtree: true });

    return () => {
      mo.disconnect(); mo2.disconnect(); ro.disconnect();
      probes.forEach((p) => p.remove());
    };
  }, [html]);

  if (!html) return null;

  // Render the configured snippet bare — the ad zone must take the exact
  // height of the ad creative: no padding, margins, borders or backgrounds.
  return <div ref={ref} className={`w-full ${className}`} />;
}
